<template>
  <list-row
    class="grouping-quiz-list-row-component row"
    :clickable="false"
  >
    <template v-if="showThumbnail && thumbnail">
      <!--
        Using a div here to take advantage of background position/size.
        We could/should use an img and the object-fit and object-position properties to do the same but it is not supported in IE11.
      -->
      <div
        v-if="thumbnail"
        class="thumbnail thumbnail-img"
        role="img"
        :aria-label="`Media for ${name}`"
        :style="{ backgroundImage: thumbnail }"
      />
      <div
        v-else
        class="thumbnail"
      />
    </template>
    <counter
      :count="grouping.flashcardAssociations.length"
      class="counter"
    />
    <pure-progress-bar
      :answered="states.length"
      :total="grouping.flashcardAssociations.length"
    />
    <div class="name">
      {{ name }}
    </div>
    <div class="action-container">
      <button
        v-if="notStarted"
        class="start"
        @click="startQuiz"
      >
        Start
      </button>
      <button
        v-if="inProgress"
        class="continue"
        @click="continueQuiz"
      >
        Continue
      </button>
      <button
        v-if="completed"
        class="results"
        @click="viewQuizResults"
      >
        Results
      </button>
      <button
        v-if="inProgress || completed"
        class="restart"
        @click="restartQuiz"
      >
        Restart
      </button>
    </div>
  </list-row>
</template>

<script>
import Counter from '@components/singletons/counter';
import ListRow from '@components/ui/listRow';
import PureProgressBar from '@/components/progress/PureProgressBar.vue';

import GlobalMixin from '@/mixins/global';

const QUIZ_STATES = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETE: 'Complete',
};

export default {
  name: 'GroupingQuizListRow',
  components: { Counter, ListRow, PureProgressBar },
  mixins: [GlobalMixin],
  props: {
    category: {
      type: Object,
    },
    grouping: {
      type: Object,
    },
    name: {
      type: String,
    },
    showThumbnail: {
      type: Boolean,
    },
    thumbnailUrl: {
      type: String,
    },
  },
  computed: {
    answeredFlashcardStates() {
      const unansweredFlashcardStatus = this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'];
      const answeredFlashcardStates = this.states.filter(state => state.flashcardStatusId !== unansweredFlashcardStatus?.id);
      return answeredFlashcardStates;
    },
    completed() {
      return this.quizState === QUIZ_STATES.COMPLETE;
    },
    inProgress() {
      return this.quizState === QUIZ_STATES.IN_PROGRESS;
    },
    notStarted() {
      return this.quizState === QUIZ_STATES.NOT_STARTED;
    },
    quizState() {
      if (this.answeredFlashcardStates.length === 0) {
        return QUIZ_STATES.NOT_STARTED;
      } else if (this.answeredFlashcardStates.length > 0) {
        return QUIZ_STATES.IN_PROGRESS;
      } else if (this.grouping.flashcardAssociations.length === this.answeredFlashcardStates.length) {
        return QUIZ_STATES.COMPLETE;
      }
    },
    route() {
      return {
        name: 'groupings#show',
        params: {
          groupingId: this.grouping.id,
        },
        query: this.currentQuery,
      };
    },
    states() {
      return this.grouping.flashcardAssociations
        .map(aa => this.$store.getters['flashcardStates/flashcardStates'][`${aa.assetBId}-asset-association-${aa.id}`])
        .filter(state => state);
    },
    thumbnail() {
      if (this.thumbnailUrl) {
        return `url("${this.thumbnailUrl}")`;
      }
    },
  },
  methods: {
    continueQuiz() {
      const flashcardIdsWithStates = this.states.map(state => state.flashcardId);
      const firstUnansweredFlashcardAssociation = this.grouping.flashcardAssociations.find(aa => !flashcardIdsWithStates.includes(aa.assetbId));
      this.$router.push({
        name: 'groupings#show',
        params: {
          categoryId: this.category?.id || 'all',
          groupingId: this.grouping.id,
          assetAssociationId: firstUnansweredFlashcardAssociation.id,
        },
        query: this.currentQuery,
      });
    },
    restartQuiz() {
      this.$store.dispatch('groupings/reset', { id: this.grouping.id }).then(() => this.startQuiz());
    },
    startQuiz() {
      this.$router.push({
        name: 'groupings#show',
        params: {
          categoryId: this.category?.id || 'all',
          groupingId: this.grouping.id,
          assetAssociationId: this.grouping.flashcardAssociations[0].id,
        },
        query: this.currentQuery,
      });
    },
    viewQuizResults() {
      this.$router.push({
        name: 'groupings#show',
        params: {
          categoryId: this.category?.id || 'all',
          groupingId: this.grouping.id,
        },
        query: this.currentQuery,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.grouping-quiz-list-row-component {
  font-size: rem-calc(14);
  color: #000000;

  .counter {
    margin-left: rem-calc(12);
  }

  .thumbnail {
    align-items: center;
    background-color: #ebedef;
    display: flex;
    flex-shrink: 0;
    height: 39px;
    justify-content: center;
    width: 70px;

    &.thumbnail-img {
      background-position: center;
      background-size: cover;
    }
  }

  @include breakpoint(small) {
    :deep(.counter-component-container),
    :deep(.progress-bar-component-container) {
      display: none;
    }
  }

  .name {
    align-items: center;
    color: #000000;
    display: flex;
    flex: 1;
    font-size: rem-calc(14);
    margin-left: rem-calc(24);
  }

  .action-container {
    display: flex;

    button {
      border-radius: 4px;
      box-sizing: border-box;
      color: white;
      font-size: rem-calc(14);
      font-weight: $semi-bold;
      margin-left: rem-calc(8);
      padding: rem-calc(6);
      text-align: center;
      width: rem-calc(72);
    }

    .restart {
      color: #354052;
      background-image: linear-gradient(to top, #f2f4f7, #ffffff);
      border: solid 1px #dfe3e9;
    }

    .continue {
      background-color: #4fb9de;
      border: solid 1px #3592B8;
    }

    .start {
      background-color: #7bd56e;
      border: solid 1px #58AE4D;
    }

    .results {
      background-color: #ffd27c;
      border: solid 1px #DDAA59;
    }
  }
}
</style>
